import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function StaffPhotos() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [kids, setKids] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/staff/children").then((r) => setKids(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  const loadPhotos = (id) => api.get(`/staff/children/${id}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
  const pick = (k) => { setSel(k); setPhotos([]); loadPhotos(k.id); };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length || !sel) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f, f.name);
        await api.post(`/staff/children/${sel.id}/photos?caption=${encodeURIComponent(caption)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} shared with parent`); setCaption(""); loadPhotos(sel.id);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const del = async (pid) => { try { await api.delete(`/staff/activity-photos/${pid}`); loadPhotos(sel.id); } catch (e) { toast.error(apiErr(e)); } };

  const filtered = kids.filter((k) => `${k.first_name} ${k.last_name} ${k.explorer_id}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">DAY GALLERY</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav(user?.role === "watcher" ? "/watcher" : ["admin", "manager"].includes(user?.role) ? "/admin" : "/staff")} data-testid="photos-back">Back</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="photos-logout"><Icons.LogOut className="w-5 h-5" /></Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search explorer…" className="rounded-xl bg-white/10 border-white/20 text-white mb-2" data-testid="photos-search" />
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {filtered.map((k) => (
              <button key={k.id} onClick={() => pick(k)} data-testid={`photos-kid-${k.id}`}
                className={`w-full text-left rounded-xl px-3 py-2 text-sm ${sel?.id === k.id ? "bg-turquoise text-navy font-bold" : "bg-white/5 hover:bg-white/10"}`}>
                {k.first_name} {k.last_name} <span className="font-mono text-xs opacity-70">{k.explorer_id}</span>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-white/40 text-sm p-2">No explorers found.</p>}
          </div>
        </div>

        <div className="md:col-span-2">
          {!sel && <p className="text-white/50 bg-white/5 rounded-xl p-6 text-center" data-testid="photos-nosel">Pick an explorer to share today's photos.</p>}
          {sel && (
            <div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-3">
                <p className="font-display font-bold">{sel.first_name} {sel.last_name}</p>
                <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Optional caption (e.g. Bouncing on the inflatables!)" className="rounded-xl bg-white/10 border-white/20 text-white my-2 text-sm" data-testid="photos-caption" />
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="photos-file-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-full bg-gold text-navy font-bold w-full" data-testid="photos-upload-btn">
                  {busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <><Icons.Camera className="w-4 h-4 mr-1" /> Upload photos</>}
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {photos.map((p) => (
                  <div key={p.id} className="relative group rounded-xl overflow-hidden bg-white/5" data-testid={`photo-${p.id}`}>
                    <img src={p.url} alt={p.caption} className="w-full h-32 object-cover" />
                    {p.caption && <p className="text-[10px] px-2 py-1 text-white/70 truncate">{p.caption}</p>}
                    <button onClick={() => del(p.id)} className="absolute top-1 right-1 bg-black/60 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`photo-del-${p.id}`}><Icons.Trash2 className="w-3.5 h-3.5 text-coral" /></button>
                  </div>
                ))}
              </div>
              {photos.length === 0 && <p className="text-white/40 text-sm text-center mt-4">No photos yet for {sel.first_name}.</p>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
